import { useState } from "react";
import type { ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/cn";
import { Avatar } from "./Avatar";

export interface NavLink {
  label: string;
  href: string;
  active?: boolean;
}

export interface AppHeaderProps {
  links: NavLink[];
  user?: { name: string; src?: string };
  /** Right-side action (e.g. "New client" button). */
  action?: ReactNode;
  /** Dark variant sits over the ink hero; light is the app shell. */
  variant?: "light" | "dark";
  className?: string;
}

/** ViccoFitness wordmark — a coral-gradient mark + bilingual-ready type. */
function Wordmark({ dark }: { dark: boolean }) {
  const { t } = useTranslation();
  return (
    <a href="#" className="flex items-center gap-2.5" aria-label={t("header.homeAria")}>
      <span className="relative grid h-9 w-9 place-items-center overflow-hidden rounded-sm bg-grad-coral shadow-volt">
        <span aria-hidden className="absolute inset-0 bg-grad-hero-glow" />
        <svg viewBox="0 0 24 24" className="relative z-10 h-5 w-5 text-white" fill="none" aria-hidden>
          <path d="M3 13h3.2l2-6.4 3.6 11.8 2.4-7.4 1.5 2h5.3" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span
        className={cn(
          "font-display text-xl font-extrabold tracking-tightest",
          dark ? "text-ondark" : "text-body",
        )}
      >
        Vicco<span className="text-coral">Fitness</span>
      </span>
    </a>
  );
}

/**
 * Sticky top navigation. Desktop shows inline links + user; mobile collapses
 * to a hamburger sheet. Keyboard accessible; the mobile sheet animates in and
 * respects reduced motion.
 */
export function AppHeader({
  links,
  user,
  action,
  variant = "light",
  className,
}: AppHeaderProps) {
  const { t } = useTranslation();
  const dark = variant === "dark";
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion();

  return (
    <header
      className={cn(
        "sticky top-0 z-40 w-full",
        dark
          ? "bg-ink/80 supports-[backdrop-filter]:bg-ink/60"
          : "border-b border-line bg-surface/85 supports-[backdrop-filter]:bg-surface/75",
        "backdrop-blur-xl backdrop-saturate-150",
        className,
      )}
    >
      <div className="mx-auto flex h-16 max-w-shell items-center justify-between px-gutter">
        <Wordmark dark={dark} />

        <nav className="hidden items-center gap-1 md:flex" aria-label={t("header.primaryNavAria")}>
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              aria-current={l.active ? "page" : undefined}
              className={cn(
                "rounded-pill px-4 py-2 font-display text-sm font-bold transition-colors duration-fast",
                l.active
                  ? dark
                    ? "bg-ondark/10 text-volt"
                    : "bg-surface text-body shadow-xs ring-1 ring-line"
                  : dark
                    ? "text-ondark-muted hover:text-ondark"
                    : "text-body-soft hover:bg-surface-sunken hover:text-body",
              )}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          {action}
          {user && <Avatar name={user.name} src={user.src} size="md" status="online" />}
        </div>

        <button
          type="button"
          className={cn(
            "grid h-10 w-10 place-items-center rounded-md md:hidden",
            dark ? "text-ondark" : "text-body",
          )}
          aria-label={open ? t("header.closeMenu") : t("header.openMenu")}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden>
            {open ? (
              <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            ) : (
              <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            )}
          </svg>
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={reduce ? false : { height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 0.61, 0.36, 1] }}
            className={cn("overflow-hidden md:hidden", dark ? "bg-ink-800" : "bg-surface border-t border-line")}
          >
            <nav className="flex flex-col gap-1 px-gutter py-4" aria-label={t("header.mobileNavAria")}>
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  aria-current={l.active ? "page" : undefined}
                  className={cn(
                    "rounded-md px-4 py-3 font-display font-bold",
                    l.active
                      ? "bg-grad-coral text-white shadow-volt"
                      : dark
                        ? "text-ondark hover:bg-ink-700"
                        : "text-body hover:bg-surface-sunken",
                  )}
                >
                  {l.label}
                </a>
              ))}
              {(action || user) && (
                <div className="mt-2 flex items-center justify-between gap-3 border-t border-line/40 pt-3">
                  {user && (
                    <span className="flex items-center gap-2">
                      <Avatar name={user.name} src={user.src} size="sm" />
                      <span className={cn("font-display text-sm font-bold", dark ? "text-ondark" : "text-body")}>
                        {user.name}
                      </span>
                    </span>
                  )}
                  {action}
                </div>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
